import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SessionService } from '@core/auth';
import { Config } from '@paragondata/ngx-config';
import { Observable, map, shareReplay } from 'rxjs';
import { HelpConfig, HelpVideo, HelpVideoCategory } from '../defs/help.interfaces';
import { VideoCategory } from '../defs/help.types';

/** Service for loading help configuration and filtering videos by role */
@Injectable({ providedIn: 'root' })
export class HelpService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly appConfig = inject(Config);

  /** Cached help configuration */
  private readonly config$ = this.http.get<HelpConfig>('assets/help.json').pipe(shareReplay(1));

  /** Signal-based access to the help configuration */
  readonly config = toSignal(this.config$, {
    initialValue: null,
  });

  /** Returns the full help configuration */
  getConfig(): Observable<HelpConfig> {
    return this.config$;
  }

  /** Returns videos filtered by the current user's roles, grouped by category */
  getVideosGroupedByCategory(): Observable<HelpVideoCategory[]> {
    return this.config$.pipe(
      map((config) => {
        const filtered = this.filterVideosByRole(config.videos);
        return this.groupByCategory(filtered, config.categoryTitles);
      }),
    );
  }

  /** Filters videos based on the current user's roles */
  private filterVideosByRole(videos: readonly HelpVideo[]): HelpVideo[] {
    const isAdmin = this.session.isAdministrator || this.session.isSuperAdministrator;
    const isPlanner = this.session.isPlanner;
    const isEmployee = this.session.isMitarbeiter;

    return videos.filter((video) => {
      if (video.roles.includes('all')) return true;
      if (isAdmin) return true;
      if (isPlanner && video.roles.includes('planning')) return true;
      if (isEmployee && video.roles.includes('employee')) return true;
      return false;
    });
  }

  /** Groups videos by category, preserving config order */
  private groupByCategory(
    videos: HelpVideo[],
    categoryTitles: Record<VideoCategory, string>,
  ): HelpVideoCategory[] {
    const categoryOrder: VideoCategory[] = ['requests', 'sickleave', 'installation'];
    const videoBaseUrl = this.appConfig.get('features.help.videoBaseUrl');

    return categoryOrder
      .map((category) => ({
        category,
        titleKey: categoryTitles[category],
        videos: videos
          .filter((v) => v.category === category)
          .map((v) => {
            const downloadUrl = `${videoBaseUrl}/${v.shareToken}/download`;
            return { ...v, src: downloadUrl, fallbackUrl: downloadUrl };
          }),
      }))
      .filter((group) => group.videos.length > 0);
  }
}
